import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Book as JournalIcon,
} from '@mui/icons-material';
import JournalEntryService from '../../services/journal-entry.service';

const JournalEntryList: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchEntries();
  }, [statusFilter]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: ['createdBy', 'postedBy'],
        'pagination[pageSize]': 100,
        sort: 'date:desc',
      };

      if (statusFilter !== 'all') {
        params['filters[status][$eq]'] = statusFilter;
      }

      const response = await JournalEntryService.getAll(params);
      setEntries(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this journal entry?')) {
      try {
        await JournalEntryService.delete(id);
        fetchEntries();
      } catch (error) {
        console.error('Error deleting journal entry:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'posted':
        return 'success';
      case 'draft':
        return 'warning';
      case 'void':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  const filteredEntries = entries.filter((entry) => {
    const attrs = entry.attributes;
    const searchLower = searchTerm.toLowerCase();
    return (
      attrs?.entryNumber?.toLowerCase().includes(searchLower) ||
      attrs?.description?.toLowerCase().includes(searchLower) ||
      attrs?.reference?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <JournalIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Journal Entries
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/journal-entries/create')}
        >
          New Entry
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Search..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by entry number, description, or reference"
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="posted">Posted</MenuItem>
            <MenuItem value="void">Void</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Entry #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No journal entries found
                </TableCell>
              </TableRow>
            ) : (
              filteredEntries.map((entry) => {
                const attrs = entry.attributes;

                return (
                  <TableRow key={entry.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {attrs?.entryNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(attrs?.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {attrs?.description}
                      </Typography>
                    </TableCell>
                    <TableCell>{attrs?.reference || '-'}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="medium">
                        {formatCurrency(attrs?.totalDebit)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="medium">
                        {formatCurrency(attrs?.totalCredit)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.status || 'draft'}
                        color={getStatusColor(attrs?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/journal-entries/${entry.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/journal-entries/${entry.id}/edit`)}
                            disabled={attrs?.status === 'posted'}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(entry.id)}
                            color="error"
                            disabled={attrs?.status === 'posted'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default JournalEntryList;

